import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { PaziaFormButton } from "components/atoms";
import { CourseApi, coursesApi } from "api";
import { FormProvider, useForm } from "react-hook-form";

import { useToaster } from "utils";
import { ToggleReadinessButton } from "components/molecules";
import { coursesStatusApi } from "api/coursesStatusApi";
import { useQueryClient } from "@tanstack/react-query";
import { QUERIES_KEYS } from "queries";
import { PaziaBox, PaziaQuill } from "components/atoms";
import { PaziaFromQuill } from "components/atoms/PaziaQuill/PaziaFromQuill";

interface BaseInformationStepProps {
  course?: CourseApi;
}
export const PreviewInformationStep = ({ course }: BaseInformationStepProps) => {
  const { showError, showSuccess } = useToaster();
  const queryClient = useQueryClient();
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      [PREVIEW_FORM_NAMES.SHORT_DESCRIPTION]: course?.data?.shortDescription ?? "",
      [PREVIEW_FORM_NAMES.DESCRIPTION]: course?.data?.description ?? "",
      [PREVIEW_FORM_NAMES.AGENDA]: course?.data?.agenda ?? "",
      [PREVIEW_FORM_NAMES.PARTICIPATION_CONDITIONS]: course?.data?.participationConditions ?? "",
      [PREVIEW_FORM_NAMES.COURSE_SCOPE]: course?.data?.courseScope ?? "",
      [PREVIEW_FORM_NAMES.ORGANIZATION_INFORMATION]: course?.data?.organizationInformation ?? "",
    },
  });

  const { handleSubmit } = methods;

  const onSubmit = async (data: CoursePreviewInformationFormSchema) => {
    if (!course) return;
    try {
      await coursesApi.updatePreviewData(course.data.id, data);
      showSuccess("Zaktualizowano dane podglądu kursanta.");
    } catch (e) {
      console.error(e);
      showError("Wystąpił błąd podczas zapisywania zmian.");
    } finally {
      await invalidate();
    }
  };

  const invalidate = async () => {
    queryClient.invalidateQueries({
      queryKey: QUERIES_KEYS.COURSE_BY_ID(Number(course?.data.id)),
    });
  };
  if (!course) return null;
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="pt-4">
        <PaziaBox className="col-span-2 mb-4 flex justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Dla kursanta</h2>
            <div className="mb-2 text-sm">
              Poniższe informacje są publicznie widoczne dla kursantów. <br /> Zostaną one wyświetlone na stronie kursu
              oraz zintegrowane z zewnętrznymi systemami, jeśli takowe istnieją.
            </div>
            <ToggleReadinessButton
              isReady={Boolean(course.data.publicDataReady)}
              onReady={coursesStatusApi.PUBLIC_DATA_READY}
              onUnready={coursesStatusApi.PUBLIC_DATA_UNREADY}
              courseId={course.data.id}
              readyText="Dane podglądu kursanta zostały zatwierdzone"
              invalidate={invalidate}
            />
          </div>
          <div>
            <PaziaFormButton title="Zapisz" type="success" />
          </div>
        </PaziaBox>
        <PaziaBox header="Krótki opis">
          <PaziaFromQuill name={PREVIEW_FORM_NAMES.SHORT_DESCRIPTION} />
        </PaziaBox>
      </form>
    </FormProvider>
  );
};

const PREVIEW_FORM_NAMES = {
  DESCRIPTION: "description",
  SHORT_DESCRIPTION: "shortDescription",
  AGENDA: "agenda",
  PARTICIPATION_CONDITIONS: "participationConditions",
  COURSE_SCOPE: "courseScope",
  ORGANIZATION_INFORMATION: "organizationInformation",
} as const;

const schema = yup
  .object({
    [PREVIEW_FORM_NAMES.DESCRIPTION]: yup.string(),
    [PREVIEW_FORM_NAMES.SHORT_DESCRIPTION]: yup.string(),
    [PREVIEW_FORM_NAMES.AGENDA]: yup.string(),
    [PREVIEW_FORM_NAMES.PARTICIPATION_CONDITIONS]: yup.string(),
    [PREVIEW_FORM_NAMES.COURSE_SCOPE]: yup.string(),
    [PREVIEW_FORM_NAMES.ORGANIZATION_INFORMATION]: yup.string(),
  })
  .required();

export type CoursePreviewInformationFormSchema = yup.InferType<typeof schema>;
